// minify 後もエラー監視などで型名が分かるよう、各クラスの name をクラスフィールドで文字列として明示する

/**
 * HTTP クライアントが投げるエラーの基底クラス。
 * `instanceof HttpError` で HTTP 通信の失敗とプログラムの不具合を見分けられる。
 * 2xx で body が JSON として読めず、HTML でもない場合は、このクラスを直接投げる。
 */
export class HttpError extends Error {
  name = 'HttpError';
}

/**
 * サーバーが 2xx 以外のステータスを返したことを表す。
 * `body` はサーバーが返した形のまま（JSON、text、空なら null）で、camelCase 化しない。
 * 4xx と 5xx は `ClientError` / `ServerError` で投げられるため、このクラスを直接受け取るのはそれ以外のステータスだけ。
 */
export class ResponseError extends HttpError {
  name = 'ResponseError';
  readonly status: number;
  readonly body: unknown;

  constructor(method: string, path: string, status: number, body: unknown) {
    super(`${method} ${path} responded ${status}`);
    this.status = status;
    this.body = body;
  }
}

/**
 * サーバーが 4xx を返したことを表す。
 * 422 のバリデーションエラーは `error instanceof ClientError && error.status === 422` で判定し、`body` から項目ごとのエラーを読む。
 * ただし 422 でも HTML（`body` は文字列）や読めない body（`null`）があるため、`body` がオブジェクトのときだけ項目のエラーとして扱う。
 */
export class ClientError extends ResponseError {
  name = 'ClientError';
}

/**
 * サーバーが 5xx を返したことを表す。
 */
export class ServerError extends ResponseError {
  name = 'ServerError';
}

/**
 * 通信そのものが失敗したことを表す（オフライン、DNS、CORS、body の読み取り中の切断など）。
 * `cause` に元の TypeError を持つ。
 */
export class NetworkError extends HttpError {
  name = 'NetworkError';
}

/**
 * リクエストの開始から body を読み終えるまでが `timeoutMs` を超えたことを表す。
 * サーバー側（ALB や Nginx）のタイムアウトは 408 の `ClientError` になり、こちらではない。
 */
export class TimeoutError extends HttpError {
  name = 'TimeoutError';
}

/**
 * JSON を期待したのに 2xx で HTML が返ったことを表す（ログイン画面へのリダイレクトなど）。
 */
export class UnexpectedHtmlError extends HttpError {
  name = 'UnexpectedHtmlError';
}

/**
 * ステータスに応じて `ClientError`（4xx）、`ServerError`（5xx）、それ以外は `ResponseError` を返す。
 */
export function responseErrorFor(
  method: string,
  path: string,
  status: number,
  body: unknown,
): ResponseError {
  if (status >= 500 && status <= 599) {
    return new ServerError(method, path, status, body);
  }
  if (status >= 400 && status <= 499) {
    return new ClientError(method, path, status, body);
  }
  return new ResponseError(method, path, status, body);
}
